import * as React from 'react';
import { BwjInputBaseComponent, IBwjInputProps, IBwjInputState } from '../bwj-input-base';
import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react';
import { BwjInput } from '../../entities/bwj-input';
import { firstOrDefault } from '../../utils/collection';

export enum RadioButtonSetOrientation {
    Horizontal,
    Vertical
}

export interface IBwjRadioButtonSetProps<T> extends IBwjInputProps<T | null> {
    options: T[];
    orientation: RadioButtonSetOrientation;
    optionDisplayKey?: string;
    optionDisplayFunc?: (option: T) => string;
    optionValueKey?: string;
    suppressValueUpdateOnOptionsChange?: boolean;
}

interface IRadioButtonSetOption<T> extends IChoiceGroupOption {
    data: T;
}

interface IState<T> extends IBwjInputState<T | null> {
    displayOptions: IRadioButtonSetOption<T>[];
    currentSelection: IRadioButtonSetOption<T> | null;
}

const horizontalLayoutStyles = {
    flexContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 20,
        flexWrap: 'wrap',
    },
};

export class BwjRadioButtonSet<T = any> extends BwjInputBaseComponent<T | null, IBwjRadioButtonSetProps<T>, IState<T>> {
    constructor(props: IBwjRadioButtonSetProps<T>) {
      super(props);

      this.handleChange = this.handleChange.bind(this);

      const displayOptions = this.buildDisplayOptions(props.options);
      // figure out what the current selection should be, if anything
      let option: any = !!this.props.input ? this.props.input.value : null;

      this.state = {
          displayOptions: displayOptions,
          currentSelection: this.getDisplayOptionWithOption(option, displayOptions),
          input: this.props.input || new BwjInput<T | null>(null),
      };
    }

    protected handleComponentDidMount() {
        const displayOptions = this.buildDisplayOptions(this.props.options);
        let option = !!this.props.input ? this.props.input.value : null;

        this.setState({
            displayOptions: displayOptions,
            currentSelection: this.getDisplayOptionWithOption(option, displayOptions),
            input: this.props.input || new BwjInput<T | null>(null)
        });
    }

    public handleComponentDidUpdate(prevProps:IBwjRadioButtonSetProps<T>) {
        // check for a change to the options, which we set on state
        if(prevProps.options !== this.props.options) {
            let changes:Partial<IState<T>> = {
                displayOptions: this.buildDisplayOptions(this.props.options)
            };

            // if a selection was already made, data about the selection may have changed
            if(!!this.state.currentSelection) {
                const currentSelection = firstOrDefault(changes.displayOptions || [], o => o.key === this.state.currentSelection?.key);

                // typically, we always trigger a value update when the options change
                // however, when suppressValueUpdateOnOptionsChange is true, we only
                // trigger a value update if the change in options caused the current selection to be lost
                // (i.e. no matching key was found in the new options set)
                if(!currentSelection || !this.props.suppressValueUpdateOnOptionsChange) {
                    changes.currentSelection = currentSelection;
                    const val = !!changes.currentSelection ? changes.currentSelection.data : null;
                    let value = new BwjInput<any>(val);
                    value.validity = prevProps.input.validity;
                    value.message = prevProps.input.message;
    
                    changes.input = value;
                    this.performFieldUpdate = true;
                }
            }

            this.setState(changes as IState<T>);
        }
        // revalidate/update the field due to a selection change.  We don't do this on blur, as we normally
        // would with other fields
        else if(this.performFieldUpdate) {
            this.performFieldUpdate = false;
            this.updateInputField();
        }
    }

    public handleChange(e?: React.FormEvent<HTMLElement | HTMLInputElement>, o?: IChoiceGroupOption) {
        const option = o as IRadioButtonSetOption<T> | undefined;
        const val = !!option ? option.data : null;
        const value = new BwjInput<T | null>(val);
        value.validity = this.state.input.validity;
        value.message = this.state.input.message;

        this.setState({
            currentSelection: option || null,
            input: value
        });
        this.performFieldUpdate = true;
    }

    protected valuesAreEqual(prev: T | null, current: T | null) {
        if(prev === current) { return true; }
        if(!prev || !current) { return false; }

        const prevOpt = this.getDisplayOptionWithOption(prev, this.state.displayOptions);
        const currentOpt = this.getDisplayOptionWithOption(current, this.state.displayOptions);

        return prevOpt === currentOpt;
    }

    protected handleValueDidChange(prev: BwjInput<T| null>, curr: BwjInput<T| null>) {
        this.setState({
            currentSelection: this.getDisplayOptionWithOption(curr.value, this.state.displayOptions)
        });
    }

    /*
    protected handleFocus() {
        // this is done to defeat a quirk with the dropdown component, which fires 'has changed' event with the first option
        // when clicked, before focus is fired.  Since we fortunately set the value on state with the old raw value on focus,
        // we do the same here to revert the ill effects of this behavior
        this.setState({
            currentSelection: this.state.currentSelection
        });
    }//*/

    private performFieldUpdate:boolean = false;

    private getDisplayOptionWithOption(option:any, displayOptions?:IRadioButtonSetOption<T>[]): IRadioButtonSetOption<T> | null {
        if(!option) { return null; }

        displayOptions = !!displayOptions ? displayOptions : this.state.displayOptions;
        const p = this.props;
        
        // get search key and return display option
        const key = !!p.optionValueKey ? option[p.optionValueKey].toString() : option.toString();
        return firstOrDefault(displayOptions, o => o.key === key);
    }

    private buildDisplayOptions(options:any[]): IRadioButtonSetOption<T>[] {
        const p = this.props;
        return options.map(o => {
            // get key
            let key = !!p.optionValueKey ? o[p.optionValueKey].toString() : o.toString();
            // get value
            let text = o.toString();
            if(!!p.optionDisplayKey) {
                text = o[p.optionDisplayKey].toString();
            }
            else if(!!p.optionDisplayFunc) {
                text = p.optionDisplayFunc(o);
            }

            return { key: key, text: text, data: o };
        });
    }
  
    public render() {
      const p = this.props;
      const s = this.state;
      return (
        <ChoiceGroup
            id={p.id}
            label={p.label}
            options={s.displayOptions}
            selectedKey={!!s.currentSelection ? s.currentSelection.key.toString() : undefined}
            onChange={this.handleChange}
            required={p.required}
            styles={p.orientation === RadioButtonSetOrientation.Horizontal ? horizontalLayoutStyles : undefined}
        />
      );
    }
  }